import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';
import { useClock, formatUtc } from './Telemetry';

const NAV_LINKS = [
  { path: '/', label: 'Home', testId: 'nav-home-link' },
  { path: '/about', label: 'About', testId: 'nav-about-link' },
  { path: '/ecosystem', label: 'Ecosystem', testId: 'nav-ecosystem-link' },
  { path: '/journal', label: 'Journal', testId: 'nav-journal-link' },
  { path: '/contact', label: 'Contact', testId: 'nav-contact-link' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const now = useClock();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="header"
      className={`sticky top-0 z-50 border-b transition-all duration-300 ${
        scrolled
          ? 'border-white/10 bg-black/60 backdrop-blur-xl py-2'
          : 'border-transparent bg-transparent py-4'
      }`}
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
          <motion.img
            src="/assets/logos/visualte-logo.png"
            alt="Visualte"
            className="h-8 w-8 object-contain"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          />
          <AnimatePresence>
            {!scrolled && (
              <motion.span
                initial={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="text-lg font-semibold tracking-[-0.02em] overflow-hidden whitespace-nowrap"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                VISUALTE
              </motion.span>
            )}
          </AnimatePresence>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((link, i) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={link.testId}
              className={`text-sm font-medium transition-colors duration-200 link-underline ${
                location.pathname === link.path
                  ? 'text-white'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              <span className="nav-index">0{i + 1}</span>{link.label}
            </Link>
          ))}
          <span className="hidden lg:inline font-mono text-[11px] tracking-[0.08em] text-white/35 tabular-nums" data-testid="header-clock">UTC {formatUtc(now)}</span>
          <Link to="/contact">
            <Button
              size="sm"
              data-testid="header-cta-button"
              className="btn-primary-gradient btn-shimmer font-semibold rounded-lg"
            >
              Discuss Business
            </Button>
          </Link>
        </nav>

        {/* Mobile menu button */}
        <button
          data-testid="mobile-menu-open-button"
          className="md:hidden p-2 text-white/70 hover:text-white"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden border-t border-white/10 bg-black/90 backdrop-blur-xl overflow-hidden"
          >
            <div className="px-4 py-4 flex flex-col gap-3">
              {NAV_LINKS.map((link, i) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-${link.testId}`}
                  className={`text-sm font-medium py-2 ${
                    location.pathname === link.path
                      ? 'text-white'
                      : 'text-white/60'
                  }`}
                >
                  <span className="nav-index">0{i + 1}</span>{link.label}
                </Link>
              ))}
              <Link to="/contact" className="mt-2">
                <Button
                  size="sm"
                  className="w-full btn-primary-gradient btn-shimmer font-semibold rounded-lg"
                >
                  Discuss Business
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
